import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, MapPin, Building2, DollarSign, FileText, CheckCircle, Briefcase } from 'lucide-react';

const RoleDetailsModal = ({ isOpen, onClose, role, onSelect, isSelected, isMaxSelected }) => {
  if (!role) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black bg-opacity-50 z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
          >
            <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-[#202356] to-[#2542D4] text-white p-6 relative">
                <button
                  onClick={onClose}
                  className="absolute top-4 right-4 text-white hover:text-gray-200 transition-colors"
                >
                  <X size={24} />
                </button>
                <h2 className="text-2xl font-bold mb-2">{role.title}</h2>
                <div className="flex flex-wrap gap-3 text-sm">
                  <div className="flex items-center gap-1">
                    <MapPin size={16} />
                    <span>{role.city ? `${role.city}, ${role.country}` : role.country}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Briefcase size={16} />
                    <span>{role.sector}</span>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
                {/* Salary and Contract */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                  <div className="bg-[#F5F8FA] border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign size={20} className="text-[#202356]" />
                      <h3 className="font-semibold text-gray-900">Salary Range</h3>
                    </div>
                    <p className="text-lg font-bold text-[#202356]">{role.salaryRange}</p>
                  </div>
                  <div className="bg-[#F5F8FA] border border-gray-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText size={20} className="text-[#202356]" />
                      <h3 className="font-semibold text-gray-900">Contract Type</h3>
                    </div>
                    <p className="text-lg font-bold text-[#202356]">{role.contractType}</p>
                  </div>
                </div>

                {/* Description */}
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Job Description</h3>
                  <p className="text-gray-700 leading-relaxed">{role.description}</p>
                </div>

                {/* Requirements */}
                {role.requirements && role.requirements.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Requirements</h3>
                    <ul className="space-y-2">
                      {role.requirements.map((req, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <CheckCircle size={16} className="text-gray-700 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Benefits */}
                {role.benefits && role.benefits.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3">Benefits</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {role.benefits.map((benefit, index) => (
                        <div key={index} className="flex items-start gap-2">
                          <CheckCircle size={14} className="text-gray-700 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 text-sm">{benefit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="border-t border-gray-200 p-6 bg-gray-50">
                <div className="flex justify-end gap-3">
                  <button
                    onClick={onClose}
                    className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-100 transition-colors"
                  >
                    Close
                  </button>
                  {isSelected ? (
                    <button
                      onClick={() => onSelect(role)}
                      className="px-6 py-3 bg-red-600 text-white rounded-lg font-medium hover:bg-red-700 transition-colors"
                    >
                      Remove from Selection
                    </button>
                  ) : (
                    <button
                      onClick={() => onSelect(role)}
                      disabled={isMaxSelected}
                      className="px-6 py-3 bg-[#202356] text-white rounded-lg font-medium hover:bg-[#2542D4] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isMaxSelected ? 'Maximum 2 Roles Selected' : 'Select This Role'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default RoleDetailsModal;
